// ---------------------------------------------------------------------------
// banners-by-type-card — donut of banner counts per type
// ---------------------------------------------------------------------------
//
// Reads GET /banners/cards-stats `.byType` and renders a donut (reusing the
// core SVG chart) + an sr-only data table. Exposes `refresh`.

import { createElement } from '../../core/dom/helpers.js';
import { donutChart } from '../../core/charts/donut-chart.js';
import { chartTable } from '../../core/charts/chart-a11y.js';
import { isAbortError } from '../../core/http/is-abort-error.js';

/**
 * @typedef {import('../../contracts/card-grid.js').CardDescriptor} CardDescriptor
 * @typedef {import('../../contracts/card-grid.js').CardHandle} CardHandle
 * @typedef {import('../factories/crud-page.js').CardsSectionContext} Ctx
 * @typedef {CardDescriptor & { render: (ctx: Ctx) => CardHandle }} BannersCard
 */

/** @returns {BannersCard} */
export function createBannersByTypeCard() {
  return {
    id: 'banners.byType',
    title: 'Banners by type',
    render(ctx) {
      return mountByType(ctx);
    },
  };
}

/** @param {Ctx} ctx @returns {CardHandle} */
function mountByType(ctx) {
  const el = createElement('div', { class: 'banners-card' });
  let destroyed = false;
  let seq = 0;

  /** @param {string} text */
  function renderMessage(text) {
    el.replaceChildren(createElement('p', { class: 'banners-card__message' }, [text]));
  }

  /** @param {{ byType?: { type: string, count: number }[] }} stats */
  function render(stats) {
    const rows = Array.isArray(stats.byType) ? stats.byType : [];
    const slices = rows.map((r) => ({ label: String(r.type ?? ''), value: Number(r.count) || 0 }));

    const media = createElement('div', { class: 'banners-card__chart' });
    // Trusted chart template; dynamic tokens escaped inside donutChart().
    media.innerHTML = donutChart(slices, { idPrefix: 'banners-by-type', title: 'Banners by type' });
    const svgEl = media.querySelector('svg');
    if (svgEl) svgEl.setAttribute('role', 'img');

    const tableWrap = createElement('div', { class: 'banners-card__table' });
    tableWrap.innerHTML = chartTable({
      caption: 'Banners by type',
      headers: ['Type', 'Count'],
      rows: slices.map((s) => [s.label, s.value]),
    });

    el.replaceChildren(media, tableWrap);
  }

  function load() {
    const mine = ++seq;
    el.setAttribute('aria-busy', 'true');
    ctx.apiClient.get('/banners/cards-stats', { signal: ctx.signal })
      .then((/** @type {unknown} */ raw) => {
        if (destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        render(/** @type {any} */ (raw) || {});
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err) || destroyed || mine !== seq) return;
        el.setAttribute('aria-busy', 'false');
        ctx.logger.error('Banners by-type load failed', { data: err });
        renderMessage('Couldn’t load chart.');
      });
  }

  renderMessage('Loading…');
  load();

  return {
    el,
    refresh: load,
    destroy() { destroyed = true; },
  };
}
